import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  Email,
  CheckCircle,
  Refresh,
} from '@mui/icons-material';
import registrationService from '../services/registrationService';

interface LocationState {
  email?: string;
  username?: string;
  userId?: string;
  returnUrl?: string;
}

/**
 * Only allow returning to our own origin or a qixit.com host so a crafted
 * ?returnUrl= cannot turn verification into an open redirect.
 */
function sanitizeReturnUrl(candidate: string | null | undefined): string {
  if (!candidate || typeof window === 'undefined') return '';
  try {
    const parsed = new URL(candidate, window.location.origin);
    if (!['http:', 'https:'].includes(parsed.protocol)) return '';
    const sameOrigin = parsed.origin === window.location.origin;
    const isQixit = parsed.hostname === 'qixit.com' || parsed.hostname.endsWith('.qixit.com');
    return sameOrigin || isQixit ? parsed.toString() : '';
  } catch {
    return '';
  }
}

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const state = location.state as LocationState | null;
  const returnUrl = sanitizeReturnUrl(state?.returnUrl || searchParams.get('returnUrl'));
  const isFramed = typeof window !== 'undefined' && window.self !== window.top;
  const isHashMailboxHost =
    typeof window !== 'undefined' &&
    ['mail.qixit.com', 'stmail.qixit.com'].includes(window.location.hostname);
  const mailboxLoginUrl =
    typeof window !== 'undefined' && window.location.hostname.includes('localhost')
      ? `${window.location.origin}/login`
      : isHashMailboxHost
        ? 'https://mail.qixit.com/#/login'
        : 'https://mail.qixit.com/login';

  const [email, setEmail] = useState(state?.email || '');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const loginPath = returnUrl ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/login';

  const openMailboxTopLevel = () => {
    if (!isFramed) {
      navigate(loginPath, {
        state: {
          message: 'Account verified successfully! Please log in.',
          email,
          returnUrl: returnUrl || undefined,
        },
      });
      return;
    }

    // A checkout overlay gave us a returnUrl: navigate this frame back to it so
    // the reader stays on the publisher page instead of landing in a stray tab.
    if (returnUrl) {
      window.location.assign(returnUrl);
      return;
    }

    const opened = window.open(mailboxLoginUrl, '_blank', 'noopener,noreferrer');
    if (opened && typeof opened.focus === 'function') {
      opened.focus();
    }

    // Best-effort close for popup/iframe contexts after opening mailbox.
    window.setTimeout(() => {
      try {
        window.close();
      } catch {
        // Ignore close failures in restricted contexts.
      }
    }, 150);
  };

  useEffect(() => {
    // If no email in state, user may have navigated directly - allow them to enter email
  }, []);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    if (!code.trim() || code.length !== 6) {
      setError('Please enter a valid 6-digit verification code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await registrationService.verifyEmailPublic(email.trim(), code.trim());
      
      if (response.success) {
        setSuccess(true);
        // In iframe contexts, open mailbox in a top-level tab/window.
        // Outside frames, keep existing delayed in-app redirect.
        if (isFramed) {
          setTimeout(() => {
            openMailboxTopLevel();
          }, 1200);
        } else {
          setTimeout(() => {
            navigate(loginPath, {
              state: {
                message: 'Account verified successfully! Please log in.',
                email: email,
                returnUrl: returnUrl || undefined,
              },
            });
          }, 3000);
        }
      } else {
        setError(response.message || 'Verification failed. Please try again.');
      }
    } catch (err: any) {
      console.error('Verification error:', err);
      setError(err.response?.data?.message || err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setResending(true);
    setError(null);
    setResendSuccess(false);

    try {
      await registrationService.resendVerificationCode(email.trim());
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err: any) {
      console.error('Resend error:', err);
      setError(err.response?.data?.message || 'Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Email Verified!
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Your account has been successfully verified.
            {isFramed
              ? ' Opening MailBox in a new tab/window...'
              : ' You will be redirected to the login page in a few seconds.'}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={openMailboxTopLevel}
            sx={{ mt: 2 }}
          >
            Open MailBox Login
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Email sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Verify Your Email
          </Typography>
          <Typography variant="body1" color="text.secondary">
            We've sent a 6-digit verification code to your email address.
            Enter the code below to complete your registration.
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Your chosen username is reserved for 30 minutes. If you do not confirm
          your email within that time, the reservation will expire and the
          username will become available for others to use.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {resendSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            A new verification code has been sent to your email.
          </Alert>
        )}

        <form onSubmit={handleVerify}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            disabled={!!state?.email}
            required
          />

          <TextField
            fullWidth
            label="Verification Code"
            value={code}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
              setCode(value);
            }}
            margin="normal"
            placeholder="Enter 6-digit code"
            inputProps={{ 
              maxLength: 6,
              style: { 
                textAlign: 'center', 
                fontSize: '1.5rem', 
                letterSpacing: '0.5em',
                fontFamily: 'monospace'
              }
            }}
            required
            autoFocus
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ mt: 3, mb: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Verify Email'}
          </Button>
        </form>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Didn't receive the code?{' '}
            <Link
              component="button"
              variant="body2"
              onClick={handleResend}
              disabled={resending}
              sx={{ cursor: resending ? 'wait' : 'pointer' }}
            >
              {resending ? (
                <>
                  <Refresh sx={{ fontSize: 14, mr: 0.5, animation: 'spin 1s linear infinite' }} />
                  Sending...
                </>
              ) : (
                'Resend Code'
              )}
            </Link>
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="body2" color="text.secondary">
            Already verified?{' '}
            <Link
              href={mailboxLoginUrl}
              underline="hover"
              target={isFramed ? '_blank' : undefined}
              rel={isFramed ? 'noopener noreferrer' : undefined}
            >
              Go to Login
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default VerifyEmail;
